import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Smile } from 'lucide-react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
}

export function EmojiPicker({ onEmojiSelect }: EmojiPickerProps) {
  const [category, setCategory] = useState<'recent' | 'people' | 'nature' | 'objects' | 'symbols'>('people');

  const categoryDisplayNames = {
    recent: 'Recent',
    people: 'Faces',
    nature: 'Nature',
    objects: 'Things',
    symbols: 'Hearts'
  };

  const emojiCategories = {
    recent: ['😊', '👍', '❤️', '😂', '😍', '🤔', '👌', '🙏'],
    people: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
      '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
      '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪',
      '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨'
    ],
    nature: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
      '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔',
      '🌸', '🌺', '🌻', '🌷', '🌹', '🌿', '🍀', '🌱'
    ],
    objects: [
      '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱',
      '📱', '💻', '⌨️', '🖨️', '🖱️', '🖲️', '💽', '💾',
      '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🥝'
    ],
    symbols: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣',
      '✅', '❌', '⭐', '🔥', '💎', '🎉', '🎊', '🎈'
    ]
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0"
        >
          <Smile className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-2" align="end" side="top">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-1 mb-2 border-b border-border pb-2">
          {Object.keys(emojiCategories).map((cat) => (
            <Button
              key={cat}
              variant={category === cat ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setCategory(cat as 'recent' | 'people' | 'nature' | 'objects' | 'symbols')}
              className="text-xs capitalize px-2 py-1 h-auto min-w-0 flex-shrink-0"
            >
              {categoryDisplayNames[cat as keyof typeof categoryDisplayNames]}
            </Button>
          ))}
        </div>
        
        {/* Emoji grid */}
        <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
          {emojiCategories[category].map((emoji, index) => (
            <Button
              key={`${emoji}-${index}`}
              variant="ghost"
              size="sm"
              onClick={() => onEmojiSelect(emoji)}
              className="h-8 w-8 p-0 text-lg hover:bg-accent"
            >
              {emoji}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}